/**
 * 把服务端的 `ActionPrompt` 接到已完成的 `ActionDock` 上。
 *
 * 三条硬约束在这里落地：
 *
 *  ★ 红线 #21 —— 能否加注**只看** `prompt.canRaise`。
 *    本文件把整个 `prompt` 原样交给 `ActionDock`（`ActionPrompt extends LegalActions`），
 *    加注按钮的去留由 `buildDockPlan` 按 `canRaise` 决定。这里**不做任何裁剪**：
 *    一旦在中间层过滤字段，就等于给「能不能加注」开了第二个判据。
 *
 *  ★ 红线 #48 —— 倒计时不进 React state。`createCountdown` 每帧现算剩余毫秒，
 *    直接写进环与秒数的 DOM 节点。整个倒计时期间零 setState、零 re-render。
 *
 *  ★ 红线 #37 —— 上行的 `playerAction` 不带 seat（协议里根本没有这个字段）；
 *    座位由服务端从连接身份解析。
 *
 * 乐观 UI 的闭环：点下去 → `ActionDock` 自己 `setPending`（按钮置灰）→
 * 服务端确认（下一帧改 view）或 `actionRejected` 到达 → 按 `clientActionId`
 * 精确回滚**那一次**并提示原因。不按 id 匹配的话，别人的拒绝会把我的按钮解锁。
 *
 * ★ 红线 #50 不在本文件：`turnChanged` 的快进由 `useTableFx` 按
 *   `actionOn === heroSeat` 触发（见 fx/useTableFx.ts）。本文件把 prompt 接进来的
 *   同时，`roomMirror` 已经把 `actorSeat` 写进了 `tableStore.view.actionOn`，
 *   那条规则因此自动成立，绝不在这里再写一份条件（两份必然漂移）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { ActionIntent, ActionPrompt, Chips, ClientMessage } from '@poker/shared'

import { ActionDock } from '../action/ActionDock.js'
import type { AllinConfirmMode } from '../action/useBetSizing.js'
import type { DockState } from '../App.js'
import { preloadSampleSfx } from '../fx/sampleSfx.js'
import { NudgeButton } from '../talk/NudgeButton.js'
import { TalkDrawer } from '../talk/TalkDrawer.js'
import { TalkEntry } from '../talk/TalkEntry.js'
import { latestAt, unreadCount } from '../talk/talkDerive.js'
import { useTalkStore } from '../talk/talkStore.js'
import { TICKER_MS, useTalkFeed } from '../talk/useTalkFeed.js'
import { useSettingsStore, useTableStore, useUiStore } from '../store/index.js'
import { selectIsActor, selectNetView, selectPrompt, useNetStore } from '../transport/applyMessage.js'
import { createCountdown } from '../transport/serverClock.js'
import { newClientActionId } from '../transport/WsTransport.js'

import { rejectionText } from './errorText.js'
import { heroIdleNote, idleDockAction } from './roomDerive.js'
import { useRoomLink } from './roomLink.js'
import './room.css'

/** 剩余时间低于它就把环切成告警色。5 秒是「还来得及点」的心理阈值。 */
const URGENT_MS = 5000

// ============================================================================
// 意图 → 上行消息
// ============================================================================

export interface WireAction {
  readonly action: 'fold' | 'check' | 'call' | 'bet' | 'raise'
  /** ★ raise-to 总额（红线 #18）；仅 bet / raise 携带 */
  readonly to?: Chips
}

/**
 * `ActionIntent`（UI 语汇）→ 协议动作。
 *
 * `allIn` 需要在这里归一：协议的动作集里没有它（服务端只认 fold/check/call/bet/raise），
 * 而 UI 的「ALL-IN」在不同局面下分别是「跟注到全下」和「加注到全下」。
 * 判据只能是 `canRaise`（红线 #21）：不能加注时全下就是 call，绝不能发一条
 * `raise{to: maxRaiseTo}` 让服务端回 `E_ACTION_NOT_REOPENED`。
 */
export function toWireAction(intent: ActionIntent, prompt: ActionPrompt): WireAction | null {
  switch (intent.type) {
    case 'fold':
      return { action: 'fold' }
    case 'check':
      return { action: 'check' }
    case 'call':
      return { action: 'call' }
    case 'bet':
    case 'raise':
      if (!prompt.canRaise) return null
      return {
        action: prompt.raiseKind === 'bet' ? 'bet' : 'raise',
        to: intent.to,
      }
    case 'allIn':
      if (!prompt.canRaise) return { action: 'call' }
      return {
        action: prompt.raiseKind === 'bet' ? 'bet' : 'raise',
        to: prompt.maxRaiseTo,
      }
  }
}

/** 组装上行帧。单独成函数是为了能脱离 React 单测。 */
export function buildActionMessage(
  intent: ActionIntent,
  prompt: ActionPrompt,
  clientActionId: string,
): ClientMessage | null {
  const wire = toWireAction(intent, prompt)
  if (wire === null) return null
  return {
    type: 'playerAction',
    handId: prompt.handId,
    // 幂等键：服务端用 (handId, actionNo) 判重，重发同一条不会打两次
    actionNo: prompt.actionNo,
    actionToken: prompt.actionToken,
    action: wire.action,
    ...(wire.to === undefined ? {} : { to: wire.to }),
    clientActionId,
  }
}

// ============================================================================
// 组件
// ============================================================================

export interface ActionDockBindingProps {
  /** 关掉预选条（默认开）。联机模式下预选是省时间的主要手段，一般不该关。 */
  readonly preAction?: boolean
  readonly allinConfirm?: AllinConfirmMode
  /**
   * 覆盖 store 里的 prompt / isActor（回放、storybook、单测用）。
   * 与 `ActionDock.legal` 是同一个约定：省略就订阅 store。
   */
  readonly prompt?: ActionPrompt | null
  readonly isActor?: boolean
  /** 本手结算等特殊阶段覆盖普通的「等待某人行动」状态。 */
  readonly statusOverride?: ReactNode
}

export function ActionDockBinding({
  preAction = true,
  allinConfirm,
  prompt: promptProp,
  isActor: isActorProp,
  statusOverride: phaseStatusOverride,
}: ActionDockBindingProps): ReactNode {
  const link = useRoomLink()
  const storePrompt = useNetStore(selectPrompt)
  const storeIsActor = useNetStore(selectIsActor)
  const prompt = promptProp === undefined ? storePrompt : promptProp
  const isActor = isActorProp === undefined ? storeIsActor : isActorProp
  const rejection = useNetStore((s) => s.lastRejection)
  const clearRejection = useNetStore((s) => s.clearRejection)
  const bigBlind = useNetStore((s) => s.view?.room.config.bigBlind ?? 1)
  const setPending = useTableStore((s) => s.setPending)
  const showToast = useUiStore((s) => s.showToast)
  const openSheet = useUiStore((s) => s.openSheet)
  const netView = useNetStore(selectNetView)

  const ringRef = useRef<HTMLDivElement | null>(null)
  const secRef = useRef<HTMLSpanElement | null>(null)
  const bindRef = useRef<HTMLDivElement | null>(null)
  /**
   * 提交时要用的 prompt。用 ref 而不是闭包变量：`ActionDock` 的预选条是在
   * 「prompt 刚到达」的那个 effect 里回调 onSubmit 的，闭包里可能还是上一步的 prompt，
   * 拿它去填 actionNo 会被服务端判成 `E_STALE_ACTION`。
   */
  const promptRef = useRef(prompt)
  promptRef.current = prompt
  /** 最近一次提交的 (clientActionId, 意图)。回滚要精确到「那一次」 */
  const submittedRef = useRef<{ id: string; intent: ActionIntent } | null>(null)

  /**
   * ★ 本设备是否持有行动权（多设备）。shadow 连接同样会收到 prompt（协议如此，
   * 这样切换设备时不用等下一次 prompt），但**不能**让它也画出按钮 ——
   * 两台手机同时能点，必然有一台的点击被拒，那是最莫名其妙的一种失败。
   */
  const actionable = isActor ? prompt : null
  const isActorRef = useRef(isActor)
  isActorRef.current = isActor

  // —— 牌桌嘴炮 ——
  // 房主关了聊天：入口气泡与抽屉都不出现（「催一下」照旧：它是节奏，不是言论）
  const talkOn = netView?.room.config.chatEnabled === true
  useTalkFeed({ ringRef, promptRef, isActorRef })
  const talkOpen = useTalkStore((s) => s.open)
  const composing = useTalkStore((s) => s.composing)
  const hasDraft = useTalkStore((s) => s.draft.trim() !== '')
  const ticker = useTalkStore((s) => s.ticker)
  const seenAt = useTalkStore((s) => s.seenAt)
  const chat = useNetStore((s) => s.chat)
  const unread = talkOpen ? 0 : unreadCount(chat, netView?.viewer.accountId ?? null, seenAt)

  useEffect(() => {
    if (ticker === null) return undefined
    const id = ticker.id
    const t = setTimeout(() => useTalkStore.getState().clearTicker(id), TICKER_MS)
    return () => clearTimeout(t)
  }, [ticker])

  const toggleTalk = useCallback((): void => {
    // 点聊天入口是一次真实激活：顺手把共享的 AudioContext 解锁、把录音预取下来（iOS 上 resume 必须在手势里）。
    // 音效关着就不碰音频：不建 AudioContext、不下载
    if (useSettingsStore.getState().sound) preloadSampleSfx()
    const s = useTalkStore.getState()
    if (s.open) {
      // 收起：输入框失焦交给抽屉自己的 effect（open=false 时 blur）
      s.setOpen(false)
      return
    }
    s.markSeen(latestAt(useNetStore.getState().chat))
    s.setOpen(true)
  }, [])

  /**
   * 加注面板和聊天抽屉锚在同一条边上：加注面板开着时聊天抽屉不能开着。
   * 打字中例外 —— 那时输入行在下注栏**下面**，两者互不遮挡，「边打字边加注」本来就是要的。
   *
   * ★ 必须是**持续**的约束，不能只在 dock 换状态的那一刻判一次：
   *   边打字边点加注 → 面板打开时还在打字（放过）→ 紧接着输入框失焦，就成了「抽屉开着、被面板藏着」；
   *   等面板一收，整块抽屉和透明遮罩突然盖在牌桌上，下一次点牌桌也被吞掉。
   */
  const [dockState, setDockState] = useState<DockState | null>(null)
  const onDockStateChange = useCallback((next: DockState): void => {
    setDockState(next)
  }, [])
  useEffect(() => {
    if (dockState === 'sheet' && talkOpen && !composing) useTalkStore.getState().setOpen(false)
  }, [dockState, talkOpen, composing])

  // —— 倒计时：直接写 DOM（红线 #48）——
  // 整个 effect 以 prompt 为键重建：新的一步 = 新的截止时刻，没有跨步复用的余地。
  useEffect(() => {
    const writeIdle = (): void => {
      const sec = secRef.current
      if (sec !== null) sec.textContent = ''
      ringRef.current?.style.setProperty('--cd-frac', '0')
    }
    if (actionable === null) {
      writeIdle()
      return undefined
    }
    const cd = createCountdown(link.clock, (ms, fraction) => {
      const ring = ringRef.current
      const sec = secRef.current
      if (ring !== null) {
        ring.style.setProperty('--cd-frac', fraction.toFixed(4))
        ring.classList.toggle('is-urgent', ms > 0 && ms <= URGENT_MS)
      }
      // 向上取整：剩 0.4s 时显示 "1" 比显示 "0" 诚实——0 出现时就该真的不能点了
      if (sec !== null) sec.textContent = ms <= 0 ? '' : String(Math.ceil(ms / 1000))
    })
    // 进度环只画到 displayDeadlineAt（§5.4）：hardDeadline 多出来的网络宽限
    // 画出来会变成「归零之后还能点 1.5 秒」，那比不画更让人困惑。
    cd.setDeadline(actionable.displayDeadlineAt, actionable.baseTimeoutMs)
    return () => {
      cd.stop()
      writeIdle()
    }
  }, [actionable, link.clock])

  /**
   * 传给 `ActionDock` 的剩余时间**只**用于「要不要跳过预选的 250ms 反悔窗口」。
   * 因此按 prompt 取一次即可，绝不能做成每帧更新的 state（那就是红线 #48 违规）。
   */
  const remainingAtPrompt = useMemo(
    () => (actionable === null ? null : link.clock.msUntil(actionable.displayDeadlineAt)),
    [actionable, link],
  )

  const submit = useCallback(
    (intent: ActionIntent): void => {
      const p = promptRef.current
      if (p === null) return
      const id = newClientActionId()
      const msg = buildActionMessage(intent, p, id)
      if (msg === null) {
        // 走到这里说明 UI 产出了一个与 canRaise 矛盾的意图：不发出去，本地直接回滚
        setPending(null)
        showToast('这一步已经不能加注了', 'warn')
        return
      }
      submittedRef.current = { id, intent }
      link.send(msg)
    },
    [link, setPending, showToast],
  )

  // —— 乐观 UI 回滚：按 clientActionId 精确匹配 ——
  useEffect(() => {
    if (rejection === null) return
    const mine = submittedRef.current
    if (mine !== null && mine.id === rejection.clientActionId) {
      // 只撤掉「按钮已锁」这一个乐观效果：牌桌状态从来没被本地改过（§11.5）
      setPending(null)
      submittedRef.current = null
      showToast(rejectionText(rejection.code, rejection.appliedBy), 'warn')
    }
    // 不是自己这一次的拒绝（多设备）就静默吞掉：弹一条别人的失败只会制造噪音
    clearRejection()
  }, [rejection, clearRejection, setPending, showToast])

  // 新的一步到达 = 上一次提交已经有了结论，回滚凭据作废
  useEffect(() => {
    submittedRef.current = null
  }, [prompt?.actionNo, prompt?.handId])

  /**
   * 牌局进行中的「坐下 / 回到牌局」。判定是纯函数（idleDockAction），这里只接动作：
   *   坐下 → 打开选座面板（SitDownSheet 在 RoomScreen 里无条件挂载，任何相位都开得了）；
   *   回到牌局 → setSitOutNextHand(false)，与等待室那颗按钮同一条命令。
   */
  const idleKind = idleDockAction(netView)
  const idleCta = useMemo(
    () =>
      idleKind === null
        ? null
        : {
            kind: idleKind,
            onPress: (): void => {
              if (idleKind === 'sit') openSheet('seatPicker')
              else link.send({ type: 'setSitOutNextHand', sitOut: false })
            },
          },
    [idleKind, openSheet, link],
  )

  const claim = useCallback((): void => {
    link.send({ type: 'claimActor' })
  }, [link])

  return (
    <div className="dockbind" ref={bindRef} data-talk={talkOn ? 'on' : 'off'}>
      {actionable !== null && (
        <div className="dockbind__timer" ref={ringRef} role="timer" aria-label="行动倒计时">
          <span className="dockbind__timer-sec num" ref={secRef} />
        </div>
      )}

      {/**
       * ★ 条件是 `!isActor`，**不带** `prompt !== null`。
       *
       * 原来只在轮到自己时才渲染，于是最常见的情形反而没有出路：
       * 你切了个标签页回来，行动权在另一个窗口，而此刻不是你的回合 ——
       * 界面上只剩一个「回大厅」，没有任何东西告诉你发生了什么、更没法拿回来。
       * §7.6 要的本来就是一条**底部固定条**，之前是漏实现而不是砍需求。
       *
       * 文案不提「设备」：服务端根本不知道对方是什么设备，而绝大多数情况下
       * 它其实是同一台机器的另一个标签页 —— 说成「另一台设备」是假话。
       */}
      {!isActor && (
        <div className="dockbind__shadow">
          <span>{prompt !== null ? '轮到你了，但操作权在你打开的另一个窗口' : '操作权在你打开的另一个窗口'}</span>
          <button type="button" className="btn btn--ghost" onClick={claim}>
            在这里继续
          </button>
        </div>
      )}

      <ActionDock
        onSubmit={submit}
        idleCta={idleCta}
        idleNote={heroIdleNote(netView)}
        idleTrail={<NudgeButton />}
        statusOverride={
          phaseStatusOverride ??
          (ticker === null ? null : (
            <span className="talk-ticker">
              <span className="talk-ticker__who">{ticker.who}：</span>
              {ticker.text}
            </span>
          ))
        }
        onDockStateChange={onDockStateChange}
        legal={actionable}
        bigBlind={bigBlind}
        preAction={preAction}
        {...(allinConfirm === undefined ? {} : { allinConfirm })}
        remainingMs={remainingAtPrompt}
      />

      {talkOn && <TalkDrawer anchorRef={bindRef} />}
      {talkOn && (
        <TalkEntry open={talkOpen} composing={composing} unread={unread} hasDraft={hasDraft} onToggle={toggleTalk} />
      )}
    </div>
  )
}

export default ActionDockBinding
